import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { X, AlertTriangle, Clock, CreditCard, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialStatusBanner({ clinic }) {
  const [trialInfo, setTrialInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTrialInfo();
    
    // Check every hour
    const interval = setInterval(loadTrialInfo, 3600000);
    return () => clearInterval(interval);
  }, [clinic]);

  const loadTrialInfo = async () => {
    try {
      if (!clinic?.id) return;
      
      const response = await axios.get(`${API_URL}/api/clinics/${clinic.id}/trial-status`);
      setTrialInfo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading trial info:', error);
      setLoading(false);
    }
  };

  const getDaysLeft = () => {
    if (!trialInfo?.trial_end_date) return null;
    const endDate = new Date(trialInfo.trial_end_date);
    const today = new Date();
    const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    return daysLeft >= 0 ? daysLeft : 0;
  };

  const daysLeft = getDaysLeft();

  // Don't show if not on trial or already dismissed
  if (loading || dismissed || !trialInfo || trialInfo.subscription_status !== 'trialing') {
    return null;
  }

  // Don't show if more than 7 days left
  if (daysLeft === null || daysLeft > 7) {
    return null;
  }

  // Determine urgency level
  const isUrgent = daysLeft <= 3;
  const isCritical = daysLeft === 0;

  return (
    <div className={`relative ${
      isCritical ? 'bg-red-600' :
      isUrgent ? 'bg-amber-500' :
      'bg-blue-600'
    } text-white`}>
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            {isCritical ? (
              <AlertTriangle className="h-5 w-5 flex-shrink-0 animate-pulse" />
            ) : (
              <Clock className="h-5 w-5 flex-shrink-0" />
            )}
            
            <div className="flex-1">
              <p className="font-semibold text-sm sm:text-base">
                {isCritical && '⏰ Your trial ends today!'}
                {isUrgent && !isCritical && `Your trial ends in ${daysLeft} days`}
                {!isUrgent && !isCritical && `${daysLeft} days left in your trial`}
              </p>
              <p className="text-xs sm:text-sm opacity-90">
                Add a payment method now to continue using My MedMember without interruption
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              onClick={() => window.location.href = '/admin/subscription'}
            >
              <CreditCard className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Add Payment</span>
              <span className="sm:hidden">Upgrade</span>
            </Button>
            
            <button
              onClick={() => setDismissed(true)}
              className="p-1 hover:bg-white/20 rounded transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
